import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { CheckCircle, Zap, BarChart3, MapPin, ClipboardList, ArrowRight, Search, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

const AUDIT_URL = "https://audit.gmb.fullscalemarketing.com.au";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] as const }
});

const benefits = [
{
  icon: Zap,
  title: "AI-Powered, 60-Second Results",
  description: "No waiting around. Our AI scans your profile and delivers a full audit in under a minute."
},
{
  icon: BarChart3,
  title: "GBP Optimisation Score",
  description: "See exactly how well your Google Business Profile is set up — and what's dragging your rankings down."
},
{
  icon: MapPin,
  title: "Local Ranking Heatmap",
  description: "Visualise how well you rank across your service area for your top keywords on a live heatmap."
},
{
  icon: ClipboardList,
  title: "Personalised Action Plan",
  description: "Get a step-by-step plan tailored to your business so you know exactly what to fix first."
}];


const steps = [
{
  number: "01",
  title: "Enter Your Business",
  description: "Submit your business name or Google Maps URL."
},
{
  number: "02",
  title: "AI Scans Your Profile",
  description: "Our AI analyses your GBP settings, content, and local rankings."
},
{
  number: "03",
  title: "Get Your Audit Report",
  description: "Receive your optimisation score, heatmap, and action plan instantly."
},
{
  number: "04",
  title: "Start Ranking Higher",
  description: "Follow your personalised plan to climb to #1 in Google Maps."
}];


export default function FreeGbpAudit() {
  return (
    <>
      <Helmet>
        <title>Free Google Business Profile Audit | FullScale Marketing</title>
        <meta
          name="description"
          content="Get a free AI-powered Google Business Profile audit. See what to fix, get a heatmap, and receive a personalized action plan to rank #1 locally." />
        
        <link rel="canonical" href="https://fullscalemarketing.com.au/free-gbp-audit" />
        <meta property="og:title" content="Free Google Business Profile Audit | FullScale Marketing" />
        <meta property="og:description" content="Get a free AI-powered Google Business Profile audit. See what to fix, get a heatmap, and receive a personalized action plan to rank #1 locally." />
        <meta property="og:url" content="https://fullscalemarketing.com.au/free-gbp-audit" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* ── Hero ── */}
      <section className="relative pt-24 sm:pt-28 lg:pt-36 pb-14 sm:pb-20 lg:pb-28 overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-primary-light to-background" />
        <div className="absolute inset-0 bg-gradient-hero-glow pointer-events-none" />
        <div className="absolute top-1/4 left-0 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-primary/[0.06] rounded-full blur-[80px] sm:blur-[120px] -translate-x-1/2" />
        <div className="absolute bottom-1/4 right-0 w-[250px] sm:w-[450px] h-[250px] sm:h-[450px] bg-primary/[0.05] rounded-full blur-[80px] sm:blur-[120px] translate-x-1/2" />
        <div className="absolute inset-0 grid-pattern opacity-20" />

        <div className="relative z-10 max-w-[720px] lg:max-w-[900px] mx-auto px-4 sm:px-6 text-center">
          {/* Badge */}
          <motion.div {...fadeUp(0)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-card border border-border/80 mb-5 shadow-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
            <span className="text-[11px] sm:text-xs text-primary tracking-wide font-bold uppercase">
              100% Free — No Credit Card Required
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            {...fadeUp(0.08)}
            className="text-[1.75rem] sm:text-[2.25rem] md:text-[2.75rem] lg:text-[3.25rem] font-extrabold text-foreground leading-[1.12] tracking-[-0.025em] mb-4 sm:mb-5">
            
            Get a Free GBP Audit to See What You Need to Fix to{" "}
            <span className="text-primary">Rank #1 on Google</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            {...fadeUp(0.14)}
            className="text-[0.9375rem] sm:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-[600px] mx-auto mb-6 sm:mb-8">
            
            AI-powered, 60-second audit that shows exactly what to fix to rank #1 locally.
          </motion.p>

          {/* CTA */}
          <motion.div {...fadeUp(0.2)}>
            <Button
              size="xl"
              asChild
              className="group w-full sm:w-auto min-w-[280px] bg-primary hover:bg-primary-dark text-primary-foreground rounded-xl shadow-button hover:shadow-button-hover hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 font-bold text-base sm:text-lg">
              
              <a href={AUDIT_URL} target="_blank" rel="noopener noreferrer">
                Get My Free GBP Audit
                <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Button>
          </motion.div>

          {/* Trust line */}
          <motion.p {...fadeUp(0.26)} className="mt-4 text-xs sm:text-sm text-muted-foreground">
            Takes 60 seconds · Instant results
          </motion.p>
        </div>
      </section>

      {/* ── Benefits ── */}
      <section className="py-14 sm:py-20 lg:py-24 bg-background">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp(0)} className="text-center mb-10 sm:mb-14">
            <h2 className="text-[1.5rem] sm:text-[1.875rem] lg:text-[2.25rem] font-extrabold text-foreground tracking-[-0.025em] mb-3">
              What You'll Get — <span className="text-primary">Completely Free</span>
            </h2>
            <p className="text-[0.9375rem] sm:text-base text-muted-foreground max-w-[520px] mx-auto">
              Everything you need to understand your local search visibility and start improving it today.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {benefits.map((benefit, i) =>
            <motion.div
              key={benefit.title}
              {...fadeUp(0.08 + i * 0.06)}
              className="group relative bg-card rounded-2xl border border-border/60 p-5 sm:p-7 hover:border-primary/30 hover:shadow-card-hover transition-all duration-300">
              
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/15 transition-colors">
                  <benefit.icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={2} />
                </div>
                <h3 className="text-[1.0625rem] sm:text-lg font-bold text-foreground mb-1.5">
                  {benefit.title}
                </h3>
                <p className="text-sm sm:text-[0.9375rem] text-muted-foreground leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-14 sm:py-20 lg:py-24 bg-muted/40">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp(0)} className="text-center mb-10 sm:mb-14">
            <h2 className="text-[1.5rem] sm:text-[1.875rem] lg:text-[2.25rem] font-extrabold text-foreground tracking-[-0.025em] mb-3">
              How It Works
            </h2>
            <p className="text-[0.9375rem] sm:text-base text-muted-foreground max-w-[480px] mx-auto">
              Four simple steps to uncover your local ranking potential.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-5">
            {steps.map((step, i) =>
            <motion.div
              key={step.number}
              {...fadeUp(0.08 + i * 0.08)}
              className="relative text-center">
              
                <div className="text-[2.5rem] sm:text-[3rem] font-extrabold text-primary/15 leading-none mb-2">
                  {step.number}
                </div>
                <h3 className="text-[1rem] sm:text-[1.0625rem] font-bold text-foreground mb-1.5">
                  {step.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {step.description}
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-14 sm:py-20 lg:py-24 bg-background-dark text-foreground-dark">
        <div className="max-w-[700px] mx-auto px-4 sm:px-6 text-center">
          <motion.div {...fadeUp(0)}>
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-primary/20 flex items-center justify-center mx-auto mb-5">
              <Search className="w-7 h-7 sm:w-8 sm:h-8 text-primary" strokeWidth={2} />
            </div>
            <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[2.25rem] font-extrabold leading-[1.15] tracking-[-0.025em] mb-3 sm:mb-4">
              Ready to See Where You <span className="text-primary">Really</span> Rank?
            </h2>
            <p className="text-[0.9375rem] sm:text-base text-foreground-dark/70 mb-6 sm:mb-8 max-w-[520px] mx-auto leading-relaxed">
              Stop guessing. Get your free audit and find out exactly what's holding your Google Business Profile back — then fix it.
            </p>
            <Button
              size="xl"
              asChild
              className="group w-full sm:w-auto min-w-[280px] bg-primary hover:bg-primary-glow text-primary-foreground rounded-xl shadow-button hover:shadow-button-hover hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 font-bold text-base sm:text-lg">
              
              <a href={AUDIT_URL} target="_blank" rel="noopener noreferrer">
                Get My Free GBP Audit
                <ArrowRight className="ml-2 w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Button>
            <p className="mt-4 text-xs sm:text-sm text-foreground-dark/50">
              Free · No signup · Results in 60 seconds
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── Internal Links ── */}
      <section className="py-10 sm:py-14 bg-background border-t border-border/40">
        <div className="max-w-[700px] mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-muted-foreground mb-3">
            Want help implementing your audit results?
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Button variant="outline" size="default" asChild className="rounded-xl">
              <Link to="/services/gbp-seo">
                <TrendingUp className="mr-2 w-4 h-4" />
                GBP & Local SEO Service
              </Link>
            </Button>
            <Button variant="ghost" size="default" asChild>
              <Link to="/">Back to Homepage</Link>
            </Button>
          </div>
        </div>
      </section>
    </>);

}